import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'

import Heading from 'components/v2/Heading'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import { getDateRange, sanitizeDateRange } from 'features/App/components/Filters/DateRangeFilter'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { sanitizeInventory } from 'resolvers/inventories'
import { SalesFilters } from './Filters'
import { SalesList } from './List'
import { SalesPagination } from './Pagination'
import { sanitizeUser } from 'resolvers/users'
import { filterEmptyValue } from 'utils/object'
import { getQueries } from 'utils/route'
import { canRead } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'

const LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'sales.helmet',
    defaultMessage: 'Sales | Nexbus Admin'
  }
})

const GET_SALES = gql`
  query GetSales(
    $limit: Int!
    $page: Int!
    $id: ID
    $status: String
    $purchaseDateRange: dateRangeInput
    $sellerId: ID
    $cartId: ID
  ) {
    sales(
      pagination: { size: $limit, page: $page }
      input: { id: $id, status: $status, purchaseDateRange: $purchaseDateRange, sellerID: $sellerId, cartID: $cartId }
    ) {
      total
      items {
        id
        status
        createdAt
        seller {
          id
          username
          firstName
          lastName
        }
        buyer {
          firstName
          lastName
          email
          seat {
            id
            label
          }
        }
        lineItems {
          itemID
          name
          quantity
          unitPrice
          total
          discount
          comboComponents {
            quantity
            item {
              id
              name
            }
          }
        }
        cart {
          id
          name
        }
        trip {
          id
          origin {
            id
            name
          }
          destination {
            id
            name
          }
          segment {
            id
            origin {
              id
              name
            }
            destination {
              id
              name
            }
          }
        }
        payment {
          amount
          method
          last4
        }
        coupon {
          code
          value
          discountType
        }
      }
    }
  }
`

const GET_USER = gql`
  query GetUser($userId: ID!) {
    user(input: { id: $userId }) {
      id
      firstName
      lastName
      username
    }
  }
`

const SalesView = (): TSFixMe => {
  const location = useLocation()
  const intl = useIntl()
  const login = useLogin()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canRead(FEATURES.SALE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.SALE} />
  }

  let {
    page = 1,
    // eslint-disable-next-line prefer-const
    id = '',
    // eslint-disable-next-line prefer-const
    status = '',
    // eslint-disable-next-line prefer-const
    purchaseDateFrom = '',
    // eslint-disable-next-line prefer-const
    purchaseDateTo = '',
    // eslint-disable-next-line prefer-const
    purchaseDateRangeValue = '',
    // eslint-disable-next-line prefer-const
    sellerId = '',
    // eslint-disable-next-line prefer-const
    cartId
  } = getQueries(location.search)

  const sanitizedPurchaseRange = sanitizeDateRange({
    from: purchaseDateFrom,
    to: purchaseDateTo,
    rangeValue: purchaseDateRangeValue
  })

  // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
  page = parseInt(page)

  const companyId = login.accessToken.companyId

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data: userData } = useQuery(GET_USER, {
    skip: !sellerId,
    variables: {
      userId: sellerId
    }
  })

  let content

  const variables = filterEmptyValue({
    limit: LIMIT,
    page: page - 1,
    id,
    status,
    sellerId,
    cartId,
    purchaseDateRange: getDateRange(sanitizedPurchaseRange)
  })

  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useQuery(GET_SALES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables
  })

  const filters = {
    id,
    status,
    seller: sanitizeUser(sellerId, userData),
    cart: sanitizeInventory(cartId, companyId),
    purchaseDateRange: sanitizedPurchaseRange
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect((): TSFixMe => {
    refetch()
  }, [])

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="spanish.sales.error.description" defaultMessage="Hubo un error al cargar tus ventas" />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="spanish.sales.error.action" defaultMessage="Recargar tus ventas" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.sales.items) && page === 1 && isEmpty(filterEmptyValue(filters))) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="spanish.sales.empty.description" defaultMessage="No tienes ventas todavía" />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.sales.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="spanish.sales.empty.filters.description" defaultMessage="No se encontró ventas" />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to="/sales" kind="primary">
            <FormattedMessage id="spanish.sales.empty.filters.action" defaultMessage="Borrar filtros" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <SalesList sales={data.sales.items} />

        <SalesPagination
          page={page}
          limit={LIMIT}
          totalItems={data.sales.total}
          currentItemsCount={data.sales.items.length}
          filters={filters}
        />
      </>
    )
  }

  return (
    <Content>
      <ContentHeader isResponsive>
        <Heading.H3>
          <FormattedMessage id="spanish.sales.header.label" defaultMessage="Ventas" />
        </Heading.H3>
      </ContentHeader>
      <ContentBody>
        <SalesFilters filters={filters} />
        <section>{content}</section>
      </ContentBody>
    </Content>
  )
}

export default withLastUrl(SalesView)
